/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Alert, Box, Chip, CircularProgress, Grid, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { DatePickerV2, DefaultSelectV2, TableV2 } from '../../../components/v2';
import { renderStatus } from 'components/v2/cell-renderers/status';
import {
  tableV2CellContentSx,
  tableV2MultilineGridSx,
  tableV2StackedCellContentSx,
} from '../../../components/v2/tableV2Styles';
import { ESTOQUE_LOCAL_OPTIONS } from './estoqueLocais';
import {
  EXTRATO_DEFAULT_PAGE_SIZE,
  fetchExtratoMovimentacao,
  mapExtratoRow,
} from './fetchExtratoMovimentacao';

const SALDO_TOOLTIP =
  'O extrato soma as entradas e saídas que ainda existem. Pedidos excluídos não entram nesta lista — o saldo final pode diferir da quantidade da tela de Estoque.';

const formatQty = (value) => {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return Number.isInteger(num) ? String(num) : String(num);
};

const formatSignedQty = (quantidade, tipo) => {
  const formatted = formatQty(quantidade);
  if (formatted === '—') return formatted;
  return tipo === 'SAIDA' ? `−${formatted}` : `+${formatted}`;
};

const formatPeriodo = (inicio, fim) => {
  const start = inicio ? moment(inicio).format('DD/MM/YYYY') : null;
  const end = fim ? moment(fim).format('DD/MM/YYYY') : null;
  if (start && end) return `${start} – ${end}`;
  if (start) return `A partir de ${start}`;
  if (end) return `Até ${end}`;
  return null;
};

const captionSx = {
  mt: 0.25,
  fontSize: '0.75rem',
  lineHeight: 1.33,
  color: 'text.secondary',
};

const descricaoSx = {
  mt: 0.5,
  pl: 1,
  borderLeft: '2px solid',
  borderColor: 'divider',
  fontSize: '0.75rem',
  lineHeight: 1.35,
  color: 'text.secondary',
  fontStyle: 'italic',
};

const ProdutoExtratoTab = ({ produtoId, initialLocal }) => {
  const [local, setLocal] = useState(initialLocal || '');
  const [inicio, setInicio] = useState(null);
  const [fim, setFim] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(EXTRATO_DEFAULT_PAGE_SIZE);
  const [mode, setMode] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [saldoExtrato, setSaldoExtrato] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setLocal(initialLocal || '');
    setPage(0);
    setMode(null);
    setRows([]);
    setSaldoExtrato(null);
    setError(null);
  }, [initialLocal, produtoId]);

  const requestPage = mode === 'page' ? page : 0;
  const requestSize = mode === 'page' ? pageSize : EXTRATO_DEFAULT_PAGE_SIZE;

  useEffect(() => {
    if (!produtoId) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchExtratoMovimentacao(produtoId, {
          page: requestPage,
          size: requestSize,
          local: local || undefined,
          dataInicio: inicio || undefined,
          dataFim: fim || undefined,
        });
        if (cancelled) return;
        setMode(result.mode);
        setRows(result.rows.map(mapExtratoRow));
        setTotalRows(result.totalRows);
        if (requestPage === 0) {
          const first = result.rows[0];
          setSaldoExtrato(first != null ? first.saldoAcumulado : null);
        }
      } catch (err) {
        if (cancelled) return;
        setRows([]);
        setTotalRows(0);
        if (requestPage === 0) setSaldoExtrato(null);
        const status = err?.response?.status;
        if (status === 404) {
          setError('O extrato ainda não está disponível neste ambiente.');
        } else {
          setError('Não foi possível carregar o extrato.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [produtoId, local, inicio, fim, requestPage, requestSize, refreshKey]);

  const columns = useMemo(
    () => [
      {
        field: 'data',
        headerName: 'Quando',
        width: 180,
        minWidth: 160,
        renderCell: ({ row }) => (
          <Box sx={tableV2StackedCellContentSx}>
            <Box sx={tableV2CellContentSx}>
              {row.data ? moment(row.data).format('DD/MM/YYYY HH:mm') : '—'}
            </Box>
            <Box sx={{ mt: 0.5 }}>
              {renderStatus({
                value: row.tipo,
                label: row.tipo === 'SAIDA' ? 'Saída' : 'Entrada',
              })}
            </Box>
          </Box>
        ),
      },
      {
        field: 'quantidade',
        headerName: 'Saldo após',
        width: 140,
        minWidth: 130,
        renderHeader: () => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>Saldo após</span>
            <Tooltip title={SALDO_TOOLTIP}>
              <InfoOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
          </Stack>
        ),
        renderCell: ({ row }) => (
          <Box sx={tableV2StackedCellContentSx}>
            <Box sx={{ ...tableV2CellContentSx, fontWeight: 600 }}>
              {formatQty(row.saldoAcumulado)}
            </Box>
            <Box sx={captionSx}>
              neste pedido {formatSignedQty(row.quantidade, row.tipo)}
            </Box>
          </Box>
        ),
      },
      {
        field: 'local',
        headerName: 'Onde',
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Box sx={tableV2StackedCellContentSx}>
            <Box sx={tableV2CellContentSx}>
              {row.local || '—'}
            </Box>
            {row.tamanhoValue ? (
              <Box sx={captionSx}>tam. {row.tamanhoValue}</Box>
            ) : null}
          </Box>
        ),
      },
      {
        field: 'usuarioNome',
        headerName: 'Usuário',
        flex: 1.2,
        minWidth: 180,
        renderCell: ({ row }) => {
          const descricao = row.descricaoPedido && String(row.descricaoPedido).trim();
          return (
            <Box sx={tableV2StackedCellContentSx}>
              <Box sx={{ ...tableV2CellContentSx, fontWeight: 600 }}>
                {row.usuarioNome || '—'}
              </Box>
              {descricao ? (
                <Box sx={descricaoSx}>{descricao}</Box>
              ) : null}
            </Box>
          );
        },
      },
      {
        field: 'pedidoId',
        headerName: 'Pedido',
        width: 90,
        sortable: false,
        renderCell: ({ row }) => (
          row.pedidoId ? (
            <Link
              href={`/v2/estoque/pedidos?id=${encodeURIComponent(row.pedidoId)}`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              Abrir
            </Link>
          ) : '—'
        ),
      },
    ],
    []
  );

  const filterData = useMemo(
    () => ({ local, inicio, fim }),
    [local, inicio, fim]
  );

  const handlePeriodoInicio = (value) => {
    setPage(0);
    setInicio(value ? value.valueOf() : null);
  };

  const handlePeriodoFim = (value) => {
    setPage(0);
    setFim(value ? value.valueOf() : null);
  };

  const localLabel = local
    || 'Todos os locais';
  const periodoLabel = formatPeriodo(inicio, fim);

  if (!produtoId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Salve o produto para ver o extrato de movimentação.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="warning">{error}</Alert>
      )}
      <Grid container spacing={1} alignItems="flex-end">
        <Grid size={{ xs: 12, sm: 6 }}>
          <DefaultSelectV2
            label="Local"
            showEmpty
            options={ESTOQUE_LOCAL_OPTIONS}
            value={local}
            onSelect={(event) => {
              setPage(0);
              setLocal(event.target.value || '');
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DatePickerV2
            label="Data início"
            value={inicio}
            onChange={handlePeriodoInicio}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DatePickerV2
            label="Data fim"
            value={fim}
            onChange={handlePeriodoFim}
          />
        </Grid>
      </Grid>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Chip size="small" label={`Local: ${localLabel}`} />
        {periodoLabel ? (
          <Chip size="small" label={`Período: ${periodoLabel}`} />
        ) : null}
        <Chip
          size="small"
          icon={loading ? <CircularProgress size={12} color="inherit" /> : undefined}
          label={loading ? 'Movimentações' : `Movimentações: ${totalRows}`}
        />
        <Chip
          size="small"
          icon={loading ? <CircularProgress size={12} color="inherit" /> : undefined}
          label={loading ? 'Saldo do extrato' : `Saldo do extrato: ${formatQty(saldoExtrato)}`}
        />
      </Stack>
      <TableV2
        rows={rows}
        columns={columns}
        loading={loading}
        autoHeight
        autoPageSize={false}
        pageSize={EXTRATO_DEFAULT_PAGE_SIZE}
        rowHeight={70}
        gridSx={tableV2MultilineGridSx}
        emptyMessage="Nenhuma movimentação encontrada"
        filterData={filterData}
        totalRows={mode === 'page' ? totalRows : undefined}
        onPageModelChange={mode === 'page' ? (model) => {
          setPage(model.page);
          setPageSize(model.pageSize);
        } : undefined}
      />
    </Stack>
  );
};

ProdutoExtratoTab.propTypes = {
  produtoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialLocal: PropTypes.string,
};

ProdutoExtratoTab.defaultProps = {
  produtoId: null,
  initialLocal: '',
};

export default ProdutoExtratoTab;
